/**
 * Scheduler Dashboard Component
 *
 * Displays a high-level overview of system health including:
 * - Execution statistics (total, succeeded, failed, running)
 * - Rate limit status
 * - Next scheduled task
 * - Average execution duration
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getExecutionStats,
  getRateLimitStatus,
  listScheduledTasks,
  type ScheduledTask
} from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Loader2, CheckCircle2, XCircle, Clock, Activity, Database, Zap } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SchedulerDashboardProps {
  className?: string;
}

export function SchedulerDashboard({ className }: SchedulerDashboardProps) {
  const today = new Date().toISOString().split('T')[0];

  // Fetch execution stats for today
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['executionStats', today],
    queryFn: () => getExecutionStats(today),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch rate limit status
  const { data: rateLimitData, isLoading: rateLimitLoading } = useQuery({
    queryKey: ['rateLimitStatus'],
    queryFn: getRateLimitStatus,
    refetchInterval: 15000 // Refresh every 15 seconds
  });

  // Fetch scheduled tasks to calculate next execution
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['scheduledTasks'],
    queryFn: listScheduledTasks,
    refetchInterval: 30000
  });

  const isLoading = statsLoading || rateLimitLoading || tasksLoading;

  // Calculate next scheduled task
  const getNextScheduledTask = (): { name: string; timeUntil: string } | null => {
    if (!tasksData?.tasks) return null;

    const enabledTasks = tasksData.tasks.filter(
      (t: ScheduledTask) => t.schedule?.enabled && t.nextExecutionTime
    );

    if (enabledTasks.length === 0) return null;

    const sortedTasks = enabledTasks.sort((a: ScheduledTask, b: ScheduledTask) => {
      return new Date(a.nextExecutionTime!).getTime() - new Date(b.nextExecutionTime!).getTime();
    });

    const nextTask = sortedTasks[0];
    const nextTime = new Date(nextTask.nextExecutionTime!);
    const now = new Date();
    const diffMs = nextTime.getTime() - now.getTime();

    if (diffMs < 0) return { name: nextTask.name || 'Unknown', timeUntil: 'now' };

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    let timeUntil: string;
    if (diffMins < 60) {
      timeUntil = `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      const remainingMins = diffMins % 60;
      timeUntil = `in ${diffHours}h ${remainingMins}m`;
    } else {
      const days = Math.floor(diffHours / 24);
      timeUntil = `in ${days} day${days !== 1 ? 's' : ''}`;
    }

    return { name: nextTask.name || 'Unknown', timeUntil };
  };

  // Format duration
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Calculate rate limit percentage
  const rateLimitPercent = rateLimitData?.data
    ? Math.round(rateLimitData.data.percentAvailable)
    : null;

  // Get rate limit bar color based on percentage
  const getRateLimitColor = (percent: number): string => {
    if (percent >= 60) return 'bg-green-500';
    if (percent >= 30) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const stats = statsData?.stats;
  const nextTask = getNextScheduledTask();

  if (isLoading) {
    return (
      <Card className={cn("mb-4", className)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("mb-4", className)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-muted-foreground">Scheduler Health</h3>
          <span className="text-xs text-muted-foreground">Today</span>
        </div>

        {/* Main Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
          {/* Total Executions */}
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-500" />
            <div>
              <div className="text-lg font-semibold">{stats?.totalExecutions ?? 0}</div>
              <div className="text-xs text-muted-foreground">executions</div>
            </div>
          </div>

          {/* Succeeded */}
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <div>
              <div className="text-lg font-semibold text-green-600">{stats?.succeeded ?? 0}</div>
              <div className="text-xs text-muted-foreground">succeeded</div>
            </div>
          </div>

          {/* Failed */}
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <div>
              <div className="text-lg font-semibold text-red-600">{stats?.failed ?? 0}</div>
              <div className="text-xs text-muted-foreground">failed</div>
            </div>
          </div>

          {/* Running */}
          <div className="flex items-center gap-2">
            <Loader2 className={cn("h-4 w-4", (stats?.running ?? 0) > 0 ? "animate-spin text-blue-500" : "text-gray-400")} />
            <div>
              <div className="text-lg font-semibold">{stats?.running ?? 0}</div>
              <div className="text-xs text-muted-foreground">running</div>
            </div>
          </div>

          {/* Average Duration */}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-purple-500" />
            <div>
              <div className="text-lg font-semibold">
                {stats?.averageDurationMs ? formatDuration(stats.averageDurationMs) : '--'}
              </div>
              <div className="text-xs text-muted-foreground">avg duration</div>
            </div>
          </div>

          {/* Records Processed */}
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-orange-500" />
            <div>
              <div className="text-lg font-semibold">
                {stats?.totalRecordsProcessed?.toLocaleString() ?? 0}
              </div>
              <div className="text-xs text-muted-foreground">records</div>
            </div>
          </div>
        </div>

        {/* Rate Limit & Next Task Row */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 pt-3 border-t">
          {/* Rate Limit */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-4 w-4 text-yellow-500" />
              <span className="text-sm text-muted-foreground">API Rate Limit</span>
              {rateLimitPercent !== null && (
                <span className={cn(
                  "text-sm font-medium",
                  rateLimitPercent >= 60 ? "text-green-600" : rateLimitPercent >= 30 ? "text-yellow-600" : "text-red-600"
                )}>
                  {rateLimitPercent}% available
                </span>
              )}
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full transition-all duration-500",
                  rateLimitPercent !== null ? getRateLimitColor(rateLimitPercent) : 'bg-gray-400'
                )}
                style={{ width: `${rateLimitPercent ?? 0}%` }}
              />
            </div>
          </div>

          {/* Next Scheduled Task */}
          {nextTask && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Next:</span>
              <span className="font-medium">&quot;{nextTask.name}&quot;</span>
              <span className="text-muted-foreground">{nextTask.timeUntil}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
